import { HttpStatusCode } from 'axios';
import { trim } from 'lodash';
import httpClient, { clientCleanup, retrieveTokenFromLocalStorage } from '@common/api/httpClient';
import httpNoAuthClient from '@common/api/httpNoAuthClient';
import paramsSerializer from '@common/api/paramsSerializer';
import { maxSuggestionsReturned } from '@common/api/rest';
import {
  type AlexaLoginRequest,
  type CurrentUserUpdateRequest,
  type GetUsersQuery,
  type PaginatedItemsResponse,
  type PostUserQuery,
  type PutUserQuery,
  type RetailerApiRequest,
  type RetailerApiResponse,
  type RetailerLevelResponse,
  RetailerType,
  type UserData,
  UserLevel,
} from '@common/types/api';
import { type GUID } from '@common/types/brandedTypes';
import type User from '@common/types/models/User';
import { createUserFromResponse } from '@common/types/models/User';
import { type ListWithCount } from '@common/utils/formatting/paginationQueryHelpers';

const allowedUmUserLevels = [
  UserLevel.SuperAdmin,
  UserLevel.CountryManager,
];

export async function fetchCurrentUser(): Promise<User> {
  const { data: user } = await httpClient.get<UserData>(
    '/auth/v1/CurrentUser',
    { preventLogout: APP_TYPE !== 'mystiebel' },
  );

  if (APP_TYPE === 'um' && !allowedUmUserLevels.includes(user.levelId)) {
    if (process.env.OPPOSITE_APP_URL) {
      window.location.assign(process.env.OPPOSITE_APP_URL);
      await new Promise(() => {}); // await forever until redirect occurred
    }
    throw new Error('error.login.restricted.user-management');
  }
  if (APP_TYPE === 'sw' && user.levelId < UserLevel.AdvancedUser) {
    const retailerLevels = user.retailers?.map(({ levelId }) => levelId) ?? [];
    if (Math.max(...retailerLevels) < UserLevel.AdvancedUser) {
      throw new Error('error.login.restricted');
    }
  }
  return createUserFromResponse(user);
}

/**
 * @throws {ApiError} `invalid-input` when the server rejects the request; for a password change
 * that means the old password is wrong.
 */
export async function updateCurrentUser(request: CurrentUserUpdateRequest) {
  const { data: user } = await httpClient.put<UserData>('/auth/v1/CurrentUser', request);
  return createUserFromResponse(user);
}

export async function verifyEmail(
  verificationToken: string,
) {
  await httpClient.put('/auth/v1/CurrentUser/verify', {}, {
    params: {
      verificationToken,
    },
    paramsSerializer,
  });
}

export async function isRestoreEnabled(userName: string, password: string): Promise<boolean> {
  try {
    const { data } = await httpClient.post(
      '/auth/v1/Users/restore/enabled',
      { userName, password },
      {
        preventLogout: true,
      },
    );
    return !!data?.restoreEnabled;
  } catch {
    return false;
  }
}

export function restoreUser(userName: string, password: string): Promise<void> {
  return httpClient.post(
    '/auth/v1/Users/restore',
    { userName, password },
    {
      preventLogout: true,
    },
  );
}

/** @throws {ApiError} `unauthorized` when the credentials are wrong (Login offers account restore). */
export async function login(userName: string, password: string): Promise<void> {
  await httpClient.post(
    '/auth/v1/Jwt/Login',
    { userName, password },
    {
      preventLogout: true,
      takeTokenFromResponse: true,
    },
  );
}

/**
 * Logs an Alexa skill user in and returns where to send them next.
 * @throws {ApiError} `forbidden` when the user's role may not use the Alexa skill.
 */
export async function loginForAlexa(
  credentials: AlexaLoginRequest,
  loginUrl = '/auth/v1/oauth/alexa/login',
): Promise<string | undefined> {
  const { data } = await httpNoAuthClient
    .post<{ redirectUrl?: string; }>(loginUrl, credentials);
  return data.redirectUrl;
}

export async function logout(): Promise<boolean> {
  try {
    const tokenData = retrieveTokenFromLocalStorage();
    const { status, data } = await httpClient.post<string>(
      '/auth/v1/Jwt/logout',
      {
        refreshToken: tokenData?.refreshToken,
        everywhere: false,
      },
    );
    const success = status === 204 && data.length === 0;
    if (success) {
      clientCleanup();
    }
    return success;
  } catch {
    return false;
  }
}

export async function fetchPartners(
  signal?: AbortSignal,
): Promise<RetailerApiResponse[]> {
  const { data } = await httpClient.get<RetailerApiResponse[]>(
    '/auth/v1/Retailers',
    { signal },
  );
  return data.map((retailer) => {
    return {
      ...retailer,
      name: trim(retailer.name),
    };
  });
}

export async function fetchRetailerLevels(
  signal?: AbortSignal,
): Promise<RetailerLevelResponse[]> {
  const { data } = await httpClient.get<RetailerLevelResponse[]>(
    '/auth/v1/RetailerLevels',
    { signal },
  );
  return data;
}

export async function fetchPartner(
  retailerId: GUID,
  signal?: AbortSignal,
): Promise<RetailerApiResponse> {
  const { data } = await httpClient.get<RetailerApiResponse>(
    `/auth/v1/Retailers/${retailerId}`,
    { signal },
  );
  return {
    ...data,
    name: trim(data.name),
  };
}

// The server reports a duplicate retailer name only as this 400 text.
const nameDuplicateRetailer = (status: number, body: unknown) =>
  status === HttpStatusCode.BadRequest && typeof body === 'string'
    && body.startsWith('Retailer name')
    && body.endsWith('already exists.')
    ? { kind: 'duplicate-retailer-name' as const }
    : undefined;

/**
 * Creates a new Retailer and returns the ID of it upon success.
 * @throws {ApiError} `conflict` when the customer number is taken; `duplicate-retailer-name` when
 * the name is taken (a 400 the standard mapping would call `invalid-input`).
 */
export async function createPartner(request: RetailerApiRequest): Promise<string> {
  const { retailerLevel, ...rest } = request;
  const { data } = await httpClient.post(
    '/auth/v1/Retailers',
    {
      ...rest,
      name: trim(request.name),
      // Retailer level can only be set when the retailer type is Installer
      retailerLevel: request.retailerType === RetailerType.Installer ? retailerLevel : null,
    },
    { nameError: nameDuplicateRetailer },
  );
  return data.id;
}

/**
 * @throws {ApiError} `conflict` when the customer number is taken; `duplicate-retailer-name` when
 * the name is taken (a 400 the standard mapping would call `invalid-input`).
 */
export function updatePartner(partner: RetailerApiResponse) {
  const {
    id,
    name,
    email,
    phoneNumber,
    countryCode,
    retailerType,
    retailerLevel,
    ...rest
  } = partner;
  return httpClient.put(
    `/auth/v1/Retailers/${id}`,
    {
      ...rest,
      name: trim(name),
      email: email || null, // NOSONAR:S6606 - Empty string should become null for API
      phoneNumber: phoneNumber || null, // NOSONAR:S6606 - Empty string should become null
      countryCode: countryCode || null, // NOSONAR:S6606 - Empty string should become null
      retailerType: typeof retailerType === 'number' ? retailerType : null,
      // Retailer level can only be set when the retailer type is Installer
      retailerLevel: retailerType === RetailerType.Installer && typeof retailerLevel === 'number'
        ? retailerLevel
        : null,
    },
    { nameError: nameDuplicateRetailer },
  );
}

export async function deletePartner(partnerId: GUID) {
  return httpClient.delete(`/auth/v2/Retailers/${partnerId}`);
}

export async function restorePartner(partnerId: GUID) {
  return httpClient.post(`/auth/v2/Retailers/${partnerId}/CancelDelete`);
}

export async function fetchUserDetailsById(
  id: string,
  signal?: AbortSignal,
): Promise<User> {
  try {
    const { data } = await httpClient.get<UserData>(`/auth/v1/Users/${id}`, { signal });
    return createUserFromResponse(data);
  } catch (error) {
    throw new Error('User details could not be loaded', { cause: error });
  }
}

export function initiatePasswordRecovery(email: string) {
  return httpClient.post(
    '/auth/v1/PasswordForgot',
    {
      email,
      confirmationUrl: `${window.location.origin}/password-create`,
    },
  );
}

const isPasswordBody = (body: unknown): body is { Password: string; } =>
  typeof body === 'object' && body !== null && 'Password' in body
  && typeof body.Password === 'string';

/** @throws {ApiError} On 400 the password rule the server rejected is the `serverMessage` (a translation id). */
export function finishPasswordRecovery(newPassword: string, token: string) {
  return httpClient.put(
    '/auth/v1/PasswordForgot',
    {
      password: newPassword,
      verificationToken: token,
    },
    {
      preventLogout: true,
      // The password rule that failed comes as { Password: '<translation key>' }
      nameError: (status, body) =>
        status === HttpStatusCode.BadRequest
          ? { serverMessage: isPasswordBody(body) ? body.Password : undefined }
          : undefined,
    },
  );
}

export async function fetchAllUsers(
  query: GetUsersQuery,
  signal?: AbortSignal,
): Promise<ListWithCount<User>> {
  const { data } = await httpClient.get<PaginatedItemsResponse<UserData>>(
    '/auth/v1/Users?includeDeactivated=true',
    {
      params: query,
      paramsSerializer,
      signal,
    },
  );
  return {
    count: data.totalCount,
    items: data.items.map(createUserFromResponse),
  };
}

export async function deleteUserAccount(user: User): Promise<void> {
  await httpClient.delete(`/auth/v1/Users/${user.id}`);
}

export async function deactivateUser(user: User): Promise<void> {
  await httpClient.post(
    `/auth/v1/Users/${user.id}/deactivate`,
    {
      sendNotification: true,
      restoreUrl: window.location.origin + process.env.PUBLIC_PATH,
    },
  );
}

export async function createUserRetailers(
  userId: string,
  retailerId: string,
  levelId: UserLevel,
) {
  await httpClient.post('/auth/v1/UserRetailers', {
    userId,
    retailerId,
    levelId,
  });
}

export async function updateUserRetailers(
  userId: string,
  retailerId: string,
  levelId: UserLevel,
) {
  await httpClient.put('/auth/v1/UserRetailers', {
    userId,
    retailerId,
    levelId,
  });
}

export async function deleteUserRetailers(
  userId: string,
  retailerId: string,
) {
  await httpClient.delete(`/auth/v1/UserRetailers/user/${userId}/retailer/${retailerId}`);
}

async function addCountryToUser(userId: string, countryCode: string): Promise<void> {
  await httpClient.post('/auth/v1/UserCountryAccesses', { userId, countryCode });
}

async function addCountriesToUser(userId: string, countryCodes: Array<string>): Promise<void> {
  if (countryCodes.length > 0) {
    const [country, ...countries] = countryCodes;
    await addCountryToUser(userId, country);
    await addCountriesToUser(userId, countries);
  }
}

async function removeCountryFromUser(userId: string, countryCode: string): Promise<void> {
  await httpClient.delete(`/auth/v1/UserCountryAccesses/${userId}/${countryCode}`);
}

async function removeCountriesFromUser(userId: string, countryCodes: Array<string>): Promise<void> {
  if (countryCodes.length > 0) {
    const [country, ...countries] = countryCodes;
    await removeCountryFromUser(userId, country);
    await removeCountriesFromUser(userId, countries);
  }
}

async function saveUserAccount(
  userId: string,
  userData: PutUserQuery,
  existingRetailerId: string | undefined,
  retailers: PostUserQuery['retailers'],
  countriesToAdd: Array<string>,
  countriesToRemove: Array<string>,
): Promise<void> {
  await httpClient.put(`/auth/v1/Users/${userId}`, {
    ...userData,
    firstName: trim(userData.firstName),
    lastName: trim(userData.lastName),
    id: userId,
  });

  if (!retailers && existingRetailerId) {
    // Retailer was removed
    await deleteUserRetailers(userId, existingRetailerId);
  }
  if (retailers) {
    if (existingRetailerId) {
      if (existingRetailerId !== retailers[0].id) {
        // Remove old retailer and add a new one
        await deleteUserRetailers(userId, existingRetailerId);
        await createUserRetailers(userId, retailers[0].id, retailers[0].levelId);
      } else {
        // Just update the level
        await updateUserRetailers(userId, existingRetailerId, retailers[0].levelId);
      }
    } else {
      // Add a new association
      await createUserRetailers(userId, retailers[0].id, retailers[0].levelId);
    }
  }
  await addCountriesToUser(userId, countriesToAdd);
  await removeCountriesFromUser(userId, countriesToRemove);
}

/** Saves the user, then their retailer and country changes; a failure in any step rejects. */
export function updateUserAccount(
  userId: string,
  userData: PutUserQuery,
  existingRetailerId: string | undefined,
  retailers: PostUserQuery['retailers'],
  countriesToAdd: Array<string> = [],
  countriesToRemove: Array<string> = [],
): Promise<void> {
  return saveUserAccount(
    userId,
    userData,
    existingRetailerId,
    retailers,
    countriesToAdd,
    countriesToRemove,
  );
}

/**
 * @throws {ApiError} `conflict` when the email is taken (the server's text is in `serverMessage`);
 * `server-error` for any 5xx.
 */
export async function createUserAccount(userData: PostUserQuery): Promise<void> {
  let passwordCreateUrl = `${process.env.OPPOSITE_APP_URL}password-create`;
  if (!/^https?:\/\//.exec(passwordCreateUrl)) {
    passwordCreateUrl = `${window.location.origin}${passwordCreateUrl}`;
  }

  let sendPasswordCreationEmail = true;
  if (userData.levelId < UserLevel.AdvancedUser) {
    const retailerLevels = userData.retailers?.map(({ levelId }) => levelId) ?? [];
    if (Math.max(...retailerLevels) < UserLevel.AdvancedUser) {
      sendPasswordCreationEmail = false;
    }
  }

  await httpClient.post(
    '/auth/v1/Users',
    {
      ...userData,
      firstName: trim(userData.firstName),
      lastName: trim(userData.lastName),
      passwordCreateUrl,
      websiteUrl: window.location.origin,
      sendPasswordCreationEmail,
    },
  );
}

export async function fetchUserIdByUsername(userName: string) {
  const { data } = await httpClient.get<string>(
    '/auth/v1/Users/username',
    {
      params: {
        userName,
      },
      paramsSerializer,
    },
  );
  return data;
}

export async function fetchEmails(prefix: string) {
  const { data } = await httpClient.get<string[]>(
    '/auth/v1/Users/Email',
    {
      params: {
        filter: prefix,
        take: maxSuggestionsReturned,
      },
      paramsSerializer,
    },
  );
  return data.toSorted((a, b) => a.localeCompare(b)).map((email) => ({
    id: email,
    value: email,
  }));
}
